/**
 * Forwarding number per salon: where "Ich möchte einen Mitarbeiter sprechen"
 * actually rings.
 *
 * Several salons: one field per salon, empty ones keep using the company-wide
 * "Salon-Telefonnummer" above. A single salon: one field, "Direkte Durchwahl
 * für Weiterleitungen" — the public salon number usually forwards to the AI
 * itself, so a transfer to it would ring the agent again; this number has to
 * ring at the reception (and is what the Rückruf-Ticket relies on when a
 * transfer fails). Salons left empty keep the default, so this is additive.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, PhoneForwarded } from 'lucide-react';
import authService from '../services/authService';

interface Salon {
  siteCd: string;
  siteNm: string;
}

/** `/profile#weiterleitung` — the agent page's Rückruf-Ticket card links here. */
export const TRANSFER_NUMBERS_ANCHOR = 'weiterleitung';

const errMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

export default function SalonTransferNumbersSection() {
  const baseId = useId();
  const { hash } = useLocation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [sites, setSites] = useState<Salon[]>([]);
  const [defaultPhone, setDefaultPhone] = useState<string | null>(null);
  // siteCd -> number as typed. Saved values are mirrored back here so the
  // dirty check compares against what the server actually stored.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await authService.getSalonTransferNumbers();
      const stored: Record<string, string> = {};
      for (const entry of data.numbers || []) {
        stored[entry.site_cd] = entry.phone_number || '';
      }
      setSites(data.sites || []);
      setDefaultPhone(data.default_phone ?? null);
      setSaved(stored);
      setDrafts(stored);
    } catch (err) {
      toast.error(errMessage(err, 'Weiterleitungsnummern konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Arrived via the link „Durchwahl einrichten“: show the section and put the cursor in.
  const visible = !loading && sites.length > 0;
  useEffect(() => {
    if (!visible || hash !== `#${TRANSFER_NUMBERS_ANCHOR}`) return;
    const section = sectionRef.current;
    if (typeof section?.scrollIntoView === 'function') section.scrollIntoView({ block: 'center' });
    section?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
  }, [visible, hash]);

  const handleSave = async (siteCd: string) => {
    const value = (drafts[siteCd] || '').trim();
    setSaving(siteCd);
    try {
      const result = await authService.upsertSalonTransferNumber(siteCd, value);
      const stored = result.phone_number || '';
      setSaved((prev) => ({ ...prev, [siteCd]: stored }));
      setDrafts((prev) => ({ ...prev, [siteCd]: stored }));
      toast.success(
        stored
          ? 'Weiterleitungsnummer gespeichert.'
          : 'Weiterleitung nutzt wieder die Standardnummer.',
      );
    } catch (err) {
      toast.error(errMessage(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(null);
    }
  };

  if (!visible) return null;

  const single = sites.length === 1;

  return (
    <div
      ref={sectionRef}
      id={TRANSFER_NUMBERS_ANCHOR}
      className="scroll-mt-24 space-y-4 rounded-lg border border-border p-4"
    >
      <div className="flex items-start gap-3">
        <PhoneForwarded className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
        <div className="space-y-1">
          {single ? (
            <>
              <h3 className="font-semibold">Direkte Durchwahl für Weiterleitungen</h3>
              <p className="text-sm text-muted-foreground">
                Möchte ein Anrufer einen Mitarbeiter sprechen, verbindet der Agent ihn mit dieser
                Nummer. Sie muss direkt an eurer Rezeption klingeln und darf nicht zurück zur KI
                weitergeleitet werden – sonst landet der Anrufer wieder beim Agenten. Bleibt das
                Feld leer, gilt die Standardnummer{defaultPhone ? ` (${defaultPhone})` : ''}.
              </p>
            </>
          ) : (
            <>
              <h3 className="font-semibold">Weiterleitung je Salon</h3>
              <p className="text-sm text-muted-foreground">
                Leitet der Agent an einen Mitarbeiter weiter, wählt er die Nummer des
                Salons, um den es im Gespräch geht. Bleibt ein Feld leer, gilt die
                Standardnummer
                {defaultPhone ? ` (${defaultPhone})` : ''}. Sind mehrere Nummern
                hinterlegt, fragt der Agent den Kunden vorher, zu welchem Salon er
                verbunden werden möchte.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {sites.map((site) => {
          const value = drafts[site.siteCd] ?? '';
          const isDirty = value.trim() !== (saved[site.siteCd] || '');
          const inputId = `${baseId}-${site.siteCd}`;
          return (
            <div key={site.siteCd} className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label
                  htmlFor={inputId}
                  className="text-xs text-muted-foreground uppercase tracking-wider font-semibold"
                >
                  {single ? 'Durchwahl der Rezeption' : site.siteNm}
                </Label>
                <Input
                  id={inputId}
                  type={single ? 'tel' : undefined}
                  inputMode={single ? 'tel' : undefined}
                  value={value}
                  onChange={(e) =>
                    setDrafts((prev) => ({ ...prev, [site.siteCd]: e.target.value }))
                  }
                  placeholder={
                    single
                      ? `+49 …${defaultPhone ? ` (leer = ${defaultPhone})` : ''}`
                      : '+49 … (leer = Standardnummer)'
                  }
                  className="w-64 bg-background border-border"
                />
              </div>
              <Button
                onClick={() => handleSave(site.siteCd)}
                disabled={saving === site.siteCd || !isDirty}
                className="bg-[#1967D2] hover:bg-[#1558b0]"
              >
                {saving === site.siteCd && (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                )}
                Speichern
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
